import { useEffect, useState } from "react";
import axios from "axios";
import Paginator from "../../components/Paginator";
import Wrapper from "../../components/Wrapper";

const Hourlystats = () => {
  const [hourlystats, setHourlystats] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalWonAmount, setTotalWonAmount] = useState(0);
  const [totalDepositAmount, setTotalDepositAmount] = useState(0);
  const [totalGGR, setTotalGGR] = useState(0);
  const [totalGGRPC, setTotalGGRPC] = useState(0);

  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // MAIN LOADER (handles normal load + search)
  const loadStats = async (pageNumber = page, from = "", to = "") => {
    try {
      let url = `hourlystats?page=${pageNumber}`;

      if (from && to) {
        url = `hourlystatsSearch?fromDateTime=${from}&toDateTime=${to}`;
      }

      const { data } = await axios.get(url);

      // handle both API formats
      if (data.data) {
        setHourlystats(data.data);
        setLastPage(data.meta?.last_page || 1);
        setTotalWonAmount(data.meta?.total_won || 0);
        setTotalDepositAmount(data.meta?.total_deposit || 0);
        setTotalGGR(data.meta?.ggr || 0);
        setTotalGGRPC(data.meta?.ggr_perc || 0);
      } else {
        setHourlystats(data);
        setLastPage(1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // DEFAULT LOAD
  useEffect(() => {
    if (!isSearching) {
      loadStats(page);
    }
  }, [page]);

  // SEARCH
  const handleSearch = async () => {
    if (!fromDateTime || !toDateTime) {
      alert("Please select both datetime values");
      return;
    }

    setIsSearching(true);
    await loadStats(1, fromDateTime, toDateTime);
  };

  // RESET
  const resetSearch = async () => {
    setFromDateTime("");
    setToDateTime("");
    setIsSearching(false);
    setPage(1);
    await loadStats(1);
  };

  const prevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const nextPage = () => {
    if (page < lastPage) setPage(page + 1);
  };

  return (
    <Wrapper pageTitle={"Hourly Stats"}>
      {/* SEARCH BAR */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs mb-1">From</label>
          <input
            type="datetime-local"
            value={fromDateTime}
            onChange={(e) => setFromDateTime(e.target.value)}
            className="px-2 py-1 border rounded"
          />
        </div>

        <div>
          <label className="block text-xs mb-1">To</label>
          <input
            type="datetime-local"
            value={toDateTime}
            onChange={(e) => setToDateTime(e.target.value)}
            className="px-2 py-1 border rounded"
          />
        </div>

        <button
          onClick={handleSearch}
          className="px-3 py-1 text-sm text-white bg-green-600 rounded hover:bg-green-700"
        >
          Search
        </button>

        <button
          onClick={resetSearch}
          className="px-3 py-1 text-sm text-white bg-gray-500 rounded hover:bg-gray-600"
        >
          Reset
        </button>
      </div>

      {/* TOP NAVIGATION */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Page <b>{page}</b> of <b>{lastPage}</b>
        </div>
        <div className="text-lg text-gray-600 dark:text-gray-300">
          Total Deposit: <b>{totalDepositAmount}</b> | Total Won:{" "}
          <b>{totalWonAmount}</b> | Total GGR: <b>{totalGGR}</b> | Total GGR%:{" "}
          <b>{totalGGRPC}%</b>
        </div>

        <div className="flex gap-2">
          <button
            onClick={prevPage}
            className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
            disabled={page === 1 || isSearching}
          >
            Prev
          </button>

          <button
            onClick={nextPage}
            className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
            disabled={page === lastPage || isSearching}
          >
            Next
          </button>

          <button
            onClick={() => loadStats(page)}
            className="px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="w-full overflow-hidden rounded-lg shadow-xs">
        <div className="w-full overflow-x-auto">
          <table className="w-full whitespace-no-wrap">
            <thead>
              <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:bg-gray-800">
                <th className="px-4 py-3">#Ref</th>
                <th className="px-4 py-3">Report Date</th>
                <th className="px-4 py-3">Report Hour</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Won Amount</th>
                <th className="px-4 py-3">GGR</th>
                <th className="px-4 py-3">GGR %</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
              {hourlystats.map((hourlystat) => (
                <tr
                  key={hourlystat.id}
                  className="text-gray-700 dark:text-gray-400"
                >
                  <td className="px-4 py-5">{hourlystat.id}</td>
                  <td className="px-4 py-3 text-sm">
                    {hourlystat.report_date}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {hourlystat.report_hour}
                  </td>
                  <td className="px-4 py-3 text-sm">{hourlystat.amount}</td>
                  <td className="px-4 py-3 text-sm">{hourlystat.won_amount}</td>
                  <td className="px-4 py-3 text-sm">{hourlystat.ggr}</td>
                  <td className="px-4 py-3 text-sm">{hourlystat.ggrpc}%</td>
                  <td className="px-4 py-3 text-sm"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* BOTTOM PAGINATION */}
        <div className="p-4">
          <Paginator lastPage={lastPage} pageChanged={setPage} />
        </div>
      </div>

      {/* BOTTOM PAGE INFO */}
      <div className="mt-3 text-sm text-center text-gray-600 dark:text-gray-400">
        Showing page {page} of {lastPage}
      </div>
    </Wrapper>
  );
};

export default Hourlystats;
